import { FC, useEffect, useMemo, useState } from 'react'

import { Button, ButtonVariants } from '@inno-ui/button'
import {
  Dropdown,
  DropdownTriggerComponents,
  DropdownValue
} from '@inno-ui/dropdown'
import { ChevronDownIcon } from '@inno-ui/icons'
import cn from 'classnames'
import { PLATFORM_NAMES } from 'modules/download-center/components/PlatformIcon'
import { detectPlatformOs } from 'modules/download-center/utils/detectPlatformOs'
import { startDownload } from 'modules/download-center/utils/startDownload'
import { useTranslation } from 'react-i18next'
import { DownloadPlatform, DownloadPlatformOs } from 'shared/api/containerApi'

import styles from './DownloadButton.module.scss'

interface Props {
  platforms: DownloadPlatform[]
  className?: string
}

const getDefaultOs = (
  platforms: DownloadPlatform[],
  detectedOs?: DownloadPlatformOs
) => platforms.find(p => p.os === detectedOs)?.os ?? platforms[0]?.os

export const DownloadButton: FC<Props> = ({ platforms, className }) => {
  const { t } = useTranslation()
  const downloadLabel = t('downloadCenter.downloadButton.download')
  const detectedOs = useMemo(() => detectPlatformOs(), [])

  const [selectedOs, setSelectedOs] = useState(() =>
    getDefaultOs(platforms, detectedOs)
  )

  useEffect(() => {
    setSelectedOs(current =>
      platforms.some(p => p.os === current)
        ? current
        : getDefaultOs(platforms, detectedOs)
    )
  }, [platforms, detectedOs])

  if (!platforms.length) {
    return (
      <div className={className}>
        <Button label={downloadLabel} variant={ButtonVariants.Dark} disabled />
      </div>
    )
  }

  const selectedPlatform =
    platforms.find(p => p.os === selectedOs) ?? platforms[0]

  const mainButton = (
    <Button
      label={t('downloadCenter.downloadButton.downloadFor', {
        label: PLATFORM_NAMES[selectedPlatform.os]
      })}
      variant={ButtonVariants.Dark}
      onClick={() => startDownload(selectedPlatform.url)}
    />
  )

  if (platforms.length === 1) {
    return <div className={className}>{mainButton}</div>
  }

  const otherPlatforms = platforms.filter(p => p.os !== selectedPlatform.os)

  return (
    <div className={cn(styles.container, styles.split, className)}>
      {mainButton}
      <Dropdown
        placeholder={downloadLabel}
        items={otherPlatforms.map(platform => ({
          label: platform.label,
          value: platform.os
        }))}
        onChange={(value: DropdownValue) => {
          if (!value || Array.isArray(value)) return
          const os = typeof value === 'string' ? value : value.value
          setSelectedOs(os as DownloadPlatformOs)
        }}
        trigger={{
          option: DropdownTriggerComponents.Button,
          props: {
            variant: ButtonVariants.Text,
            label: t('downloadCenter.downloadButton.otherPlatforms'),
            trailingIcon: () => <ChevronDownIcon color='#2186AB80' />
          }
        }}
      />
    </div>
  )
}
